import { Ref, type Actor, type Agent, type AgentId, type Conversation, type Message, type Part } from "@agentsaas/protocol";

/**
 * 聊天室的纯逻辑：@ 解析与补全、消息合并排序、typing 状态、护栏信息、附件引用。
 * 组件只负责渲染，逻辑都在这里单测。
 */

export type GuardReason = "hop_limit" | "budget" | "loop_detected" | "manual";

/** @ 名字允许的字符（与 Agent.name 的正则一致） */
const NAME = "[\\p{L}\\p{N}_-]";
const MENTION_RE = new RegExp(`(^|[^\\p{L}\\p{N}_-])@(${NAME}+)`, "gu");

/** 从文本里找出 @ 到的成员 agent（大小写不敏感，去重，按出现顺序） */
export function parseMentions(text: string, agents: Pick<Agent, "id" | "name">[]): AgentId[] {
  const byName = new Map(agents.map((a) => [a.name.toLowerCase(), a.id]));
  const out: AgentId[] = [];
  for (const m of text.matchAll(MENTION_RE)) {
    const id = byName.get(m[2]!.toLowerCase());
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

/** 把文本切成普通片段与 @ 片段（渲染高亮用）；known 为可识别的名字 */
export function splitMentions(text: string, known: string[]): { text: string; mention?: boolean }[] {
  const names = new Set(known.map((n) => n.toLowerCase()));
  const out: { text: string; mention?: boolean }[] = [];
  let last = 0;
  for (const m of text.matchAll(MENTION_RE)) {
    const at = m.index! + m[1]!.length;
    if (!names.has(m[2]!.toLowerCase())) continue;
    if (at > last) out.push({ text: text.slice(last, at) });
    out.push({ text: `@${m[2]}`, mention: true });
    last = at + 1 + m[2]!.length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

/** 光标处正在输入的 @ 查询：返回 { start（@ 的位置）, query } 或 null */
export function activeMention(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const m = new RegExp(`(^|[^\\p{L}\\p{N}_-])@(${NAME}*)$`, "u").exec(before);
  if (!m) return null;
  return { start: before.length - m[2]!.length - 1, query: m[2]! };
}

/** 补全候选：名字前缀优先，其次包含 */
export function mentionCandidates<T extends Pick<Agent, "name">>(agents: T[], query: string, limit = 6): T[] {
  const q = query.toLowerCase();
  const prefix = agents.filter((a) => a.name.toLowerCase().startsWith(q));
  const contains = agents.filter((a) => !a.name.toLowerCase().startsWith(q) && a.name.toLowerCase().includes(q));
  return [...prefix, ...contains].slice(0, limit);
}

/** 选中候选后替换 @query，返回新文本与光标位置 */
export function applyMention(text: string, caret: number, name: string): { text: string; caret: number } {
  const a = activeMention(text, caret);
  if (!a) return { text, caret };
  const insert = `@${name} `;
  const next = text.slice(0, a.start) + insert + text.slice(caret).replace(/^ /, "");
  return { text: next, caret: a.start + insert.length };
}

/** 按 id 去重合并，按时间正序（同一毫秒按 id） */
export function mergeMessages(cur: Message[], incoming: Message[]): Message[] {
  const map = new Map(cur.map((m) => [m.id, m]));
  for (const m of incoming) map.set(m.id, m);
  return [...map.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
}

export const textOfParts = (parts: Part[]) =>
  parts
    .filter((p): p is Extract<Part, { type: "text" }> => p.type === "text")
    .map((p) => p.text)
    .join("\n");

export const refsOfParts = (parts: Part[]) => parts.filter((p): p is Extract<Part, { type: "ref" }> => p.type === "ref");

export const sameActor = (a: Actor, b: Actor) => a.type === b.type && (a.type === "system" || (b.type !== "system" && a.id === b.id));

/** 房间的成员 agent（按项目 agent 列表解析，未知的忽略） */
export function memberAgents(conv: Pick<Conversation, "members">, agents: Agent[]): Agent[] {
  const ids = new Set(conv.members.filter((m) => m.type === "agent").map((m) => m.id as string));
  return agents.filter((a) => ids.has(a.id));
}

/** 房间标题：有标题用标题，否则用成员 agent 名字 */
export function roomTitle(conv: Pick<Conversation, "title" | "members">, agents: Agent[]): string {
  if (conv.title) return conv.title;
  return memberAgents(conv, agents)
    .map((a) => a.name)
    .join(", ");
}

/** 预算进度（0–1，无预算 = null） */
export function budgetRatio(conv: Pick<Conversation, "budgetMicroUsd" | "usedMicroUsd">): number | null {
  if (conv.budgetMicroUsd === null) return null;
  if (conv.budgetMicroUsd === 0) return 1;
  return Math.min(1, conv.usedMicroUsd / conv.budgetMicroUsd);
}

/** typing：agentId → 过期时间；收到该 agent 的消息或超时后移除 */
export type TypingState = Record<string, number>;
export const TYPING_TTL_MS = 6000;

export function typingOn(s: TypingState, agentId: string, now = Date.now()): TypingState {
  return { ...s, [agentId]: now + TYPING_TTL_MS };
}

export function typingOff(s: TypingState, agentId: string): TypingState {
  if (!(agentId in s)) return s;
  const { [agentId]: _gone, ...rest } = s;
  return rest;
}

export function typingActive(s: TypingState, now = Date.now()): string[] {
  return Object.entries(s)
    .filter(([, until]) => until > now)
    .map(([id]) => id);
}

/** 附件引用：git://repo@commit:path | asset://id@ver | shared://path | work://path */
export function isRef(v: string): boolean {
  return Ref.safeParse(v.trim()).success;
}

/** asset://ast_xxx@3 → { assetId, version } */
export function parseAssetRef(ref: string): { assetId: string; version: number | null } | null {
  const m = /^asset:\/\/(ast_[0-9A-Z]+)(?:@(\d+))?$/.exec(ref);
  return m ? { assetId: m[1]!, version: m[2] ? Number(m[2]) : null } : null;
}

/** 引用的显示名：有 name 用 name，否则取路径最后一段 */
export function refLabel(p: { ref: string; name?: string }): string {
  if (p.name) return p.name;
  const tail = p.ref.replace(/^[a-z]+:\/\//, "").split(/[/:]/).filter(Boolean).pop();
  return tail ?? p.ref;
}
